import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Plus, Copy, ArrowLeft, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { HelperRolePicker } from "@/components/HelperRolePicker";

// Iter235b — Owner + Festival Deputy quick-generate modal. Skip the
// full /helpers admin page when you just need to onboard a helper on
// the fly during live ops. Same POST /helpers/passes call under the
// hood so passes still appear in the master list.

// Iter326 · Bug 2 — Duplicate "Festival Deputy" role removed from the
// dropdown. Legacy passes still map to a Deputy label via ROLE_META.
const ROLE_META = {
  check_in_helper: { label: "Check-In Only", color: "#66C2FF", scope: "Names + tap check-in + player QR" },
  court_ops_helper: { label: "Court Operations", color: "#CCFF00", scope: "Generate games, tick statuses, drag/drop + check-in" },
  deputy_helper: { label: "Deputy", color: "#B26BFF", scope: "OPS + Player Manager (roster, ratings, import) — blocked from billing/settings" },
  festival_only_helper: { label: "Deputy", color: "#B26BFF", scope: "OPS + Player Manager (roster, ratings, import) — blocked from billing/settings" },
  // Iter431/437 — OPS Assist helper (internal name unchanged).
  roster_ops_helper: { label: "OPS Assist", color: "#FFB020", scope: "Registration + Resting roster only — mark Available, tap Assign to Coaching (right-click on desktop)" },
};
const CREATE_ROLES = ["check_in_helper", "court_ops_helper", "roster_ops_helper", "deputy_helper"];

const PRESETS_KEY = "rl_helper_pass_presets";

export const SHIFT_OPTIONS = [
  ...[2, 4, 8, 12, 24].map((h) => ({ value: h, label: `${h} hours` })),
  { value: 0, label: "Until end of festival" },
];
export const shiftShort = (h) => (h === 0 ? "to end" : `${h}h`);
function loadPresets() {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || "[]"); } catch { return []; }
}
function savePresets(list) {
  try { localStorage.setItem(PRESETS_KEY, JSON.stringify(list.slice(0, 10))); } catch {}
}

export default function HelperPassQuickModal({ festivalId, open, onClose, licenseActive = true }) {
  const [form, setForm] = useState({ role: "check_in_helper", label: "", hours: 4 });
  const [busy, setBusy] = useState(false);
  const [pass, setPass] = useState(null);
  const [presets, setPresets] = useState(loadPresets);
  const backendOrigin = useMemo(() => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api\/?$/, ""), []);

  const applyPreset = (p) => setForm({ role: p.role, label: p.label, hours: p.hours });
  const savePresetFromForm = () => {
    if (!form.label.trim()) { toast.error("Give it a label first"); return; }
    const dedup = presets.filter((p) => !(p.label === form.label && p.role === form.role && p.hours === form.hours));
    const next = [{ label: form.label.trim(), role: form.role, hours: form.hours, id: Date.now() }, ...dedup];
    setPresets(next); savePresets(next);
    toast.success("Preset saved");
  };
  const deletePreset = (id) => {
    const next = presets.filter((p) => p.id !== id);
    setPresets(next); savePresets(next);
  };

  if (!open) return null;

  const generate = async () => {
    if (!form.label.trim()) { toast.error("Give the pass a label (e.g. \"Kim @ front desk\")"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/festival/${festivalId}/helpers/passes`, {
        role: form.role, label: form.label.trim(), hours: form.hours, until_end: form.hours === 0,
      });
      setPass(r.data);
    } catch (e) {
      // Iter328 · Bug 2b — Pydantic v2 validation errors return an
      // array of `{type, loc, msg, ...}` objects that React can't
      // render directly. `formatApiError` normalises to a string.
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  const close = () => { setPass(null); setForm({ role: "check_in_helper", label: "", hours: 4 }); onClose?.(); };
  const another = () => { setPass(null); setForm({ ...form, label: "" }); };
  const copyText = (v) => navigator.clipboard?.writeText(v).then(
    () => toast.success("Copied"), () => window.prompt("Copy this", v),
  );

  const redeemUrl = pass ? `${backendOrigin}${pass.redeem_url}?code=${pass.code}` : "";

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" data-testid="helper-quick-modal">
      <div className="w-full max-w-xl bg-[#0A0A0A] border-2 border-[#CCFF00] p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#CCFF00]" />
            <div>
              <div className="label-mono text-[#CCFF00]">QUICK HELPER PASS</div>
              <h2 className="h-display text-xl">{pass ? "Show this to your helper" : "Onboard a helper"}</h2>
            </div>
          </div>
          <button type="button" onClick={close} data-testid="helper-quick-close" className="text-[#A3A3A3] hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!pass && (
          <div data-testid="helper-quick-form">
            {!licenseActive && (
              <div
                className="border border-[#FFB020] bg-[#1A140A] p-2 mb-3 flex items-start gap-2"
                data-testid="helper-quick-locked-banner"
              >
                <X className="w-4 h-4 text-[#FFB020] shrink-0 mt-0.5" />
                <div className="text-xs text-white">
                  <div className="label-mono text-[#FFB020] text-[10px]">PAYMENT REQUIRED</div>
                  Helper passes and QR codes are locked until the festival is activated.
                  You can still plan the shift below — Generate unlocks once you buy an
                  activation pass or receive complimentary access.
                </div>
              </div>
            )}            {presets.length > 0 && (
              <div className="mb-3" data-testid="helper-quick-presets">
                <div className="text-[10px] uppercase tracking-widest text-[#525252] mb-1">Presets</div>
                <div className="flex flex-wrap gap-1">
                  {presets.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 border border-[#262626] text-[10px]">
                      <button
                        type="button"
                        onClick={() => applyPreset(p)}
                        data-testid={`helper-quick-preset-${p.id}`}
                        className="px-2 py-0.5 uppercase tracking-widest text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
                        title={`${p.role} · ${shiftShort(p.hours)}`}
                      >
                        {p.label} · {shiftShort(p.hours)}
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePreset(p.id)}
                        aria-label="Delete preset"
                        className="px-1 text-[#525252] hover:text-[#FF4500]"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1 mt-2">Label</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Kim @ front desk"
              autoFocus
              data-testid="helper-quick-label"
              className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm"
            />
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1 mt-3">Role</label>
            <HelperRolePicker
              value={form.role}
              onChange={(role) => setForm({ ...form, role })}
              roles={CREATE_ROLES}
              roleMeta={ROLE_META}
              testId="helper-quick-role"
            />
            <p className="text-[10px] text-[#525252] mt-1">{ROLE_META[form.role].scope}</p>
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1 mt-3">Shift length</label>
            <select
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
              data-testid="helper-quick-hours"
              className="w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm"
            >
              {SHIFT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="mt-4 flex justify-between items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={savePresetFromForm}
                data-testid="helper-quick-save-preset"
                disabled={!form.label.trim()}
                className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#CCFF00] disabled:opacity-40"
                title="Save this label + role + shift length as a one-tap preset"
              >
                + Save as preset
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={close}
                  data-testid="helper-quick-cancel"
                  className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={generate}
                  disabled={busy || !form.label.trim() || !licenseActive}
                  data-testid="helper-quick-generate"
                  title={!licenseActive ? "Activate the festival to unlock helper passes" : undefined}
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" /> {busy ? "Creating…" : licenseActive ? "Generate pass" : "Locked — activate first"}
                </button>
              </div>
            </div>
          </div>
        )}

        {pass && (
          <div data-testid="helper-quick-reveal">
            <div className="flex gap-4 flex-wrap items-start">
              <div className="bg-white p-3 shrink-0">
                <QRCodeSVG value={redeemUrl} size={360} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 180, height: 180, imageRendering: "pixelated" }} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest text-[#525252]">{pass.label}</div>
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: ROLE_META[pass.role]?.color }}>
                  {ROLE_META[pass.role]?.label} · expires {new Date(pass.expires_at).toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Access code</div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl font-mono tracking-[0.25em] text-[#CCFF00]" data-testid="helper-quick-code">{pass.code}</span>
                  <button type="button" onClick={() => copyText(pass.code)} className="p-1 text-[#A3A3A3] hover:text-[#CCFF00]" title="Copy code">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => copyText(redeemUrl)}
                  data-testid="helper-quick-copy-link"
                  className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" /> Copy full redeem link
                </button>
              </div>
            </div>
            <div className="mt-4 flex justify-between gap-2 border-t border-[#262626] pt-3">
              <button
                type="button"
                onClick={another}
                data-testid="helper-quick-another"
                className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> Generate another
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="helper-quick-done"
                className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
